import { useCallback, useState } from 'react';
import { useAdmin } from '../../context/useAdmin';
import type { AdminStore } from '../../lib/access';

/**
 * One save or delete at a time, with what came of it. Every admin page has the
 * same three things to show — busy, an error, a confirmation — so they share
 * the bookkeeping.
 */
export function useAdminAction() {
  const { commit } = useAdmin();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = useCallback(
    async (call: () => Promise<AdminStore>, done: string): Promise<boolean> => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await commit(call());
        setNotice(done);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That did not work. Try again.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [commit],
  );

  const clear = useCallback(() => {
    setError(null);
    setNotice(null);
  }, []);

  return { busy, error, notice, run, clear };
}
